import axios from "axios";
import { useEffect, useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { formatDate } from "../../../utils/dateFormatter";
import { API_URL } from "../../../config";
import { AuthContext } from "../../../Context/AuthContext";
import "./ClientsDashboard.css";

const canDo = (user, module, action) => {
  if (user?.role === "superadmin" || user?.role === "manager") return true;
  return user?.permissions?.[module]?.[action] === true;
};

const canViewPage = (user, module) => {
  if (user?.role === "superadmin" || user?.role === "manager") return true;
  return user?.permissions?.[module]?.view === true;
};

function Clients() {
  const [Client, setClient] = useState([]);
  const [search, setSearch] = useState("");
  const [filtered, setFiltered] = useState([]);

  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/getClientData`)
      .then((res) => {
        if (res.data) {
          setClient(res.data);
          setFiltered(res.data);
        } else {
          alert("Failed to load clients");
        }
      })
      .catch((err) => console.error(err));
  }, []);

  const handleDelete = async (leadId) => {
    if (!canDo(user, "clients", "delete")) {
      return alert("You do not have permission to delete clients.");
    }

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this client?"
    );
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API_URL}/api/DeleteLead/${leadId}`);

      const updated = Client.filter((c) => c.leadId !== leadId);
      setClient(updated);
      setFiltered(updated);

      alert("Client deleted successfully!");
    } catch (error) {
      console.error(error);
      alert("Failed to delete client");
    }
  };

  if (!canViewPage(user, "clients")) {
    return (
      <div className="container py-5 text-center">
        <h2 className="text-danger fw-bold">Access Denied</h2>
        <p>You do not have permission to view Clients.</p>
      </div>
    );
  }

  const searchedClients = filtered.filter((c) => {
    const q = search.toLowerCase();
    return (
      c.leadId?.toLowerCase().includes(q) ||
      c.leadName?.toLowerCase().includes(q) ||
      c.emailId?.toLowerCase().includes(q) ||
      c.phoneNo?.toLowerCase().includes(q) ||
      c.department?.deptName?.toLowerCase().includes(q) ||
      c.service?.serviceName?.toLowerCase().includes(q) ||
      c.accountNo?.toLowerCase().includes(q) ||
      c.ifscCode?.toLowerCase().includes(q) ||
      c.gstNumber?.toLowerCase().includes(q)
    );
  });

  const getProjectPriceForList = (client) => {
    const projects = Array.isArray(client?.projects) ? client.projects : [];
    if (projects.length > 0) {
      const total = projects.reduce(
        (sum, p) => sum + Number(p?.project_price ?? p?.price ?? 0),
        0
      );
      return total;
    }
    return Number(client?.project_price ?? 0);
  };

  const exportExcel = () => {
    const rows = searchedClients.map((c) => ({
      ID: c.leadId || "",
      Name: c.leadName || "",
      Email: c.emailId || "",
      Phone: c.phoneNo || "",
      Department: c.department?.deptName || "",
      Service: c.service?.serviceName || "",
      Project_Price: getProjectPriceForList(c),
      Enroll_Date: formatDate(c.date),
      Account_Number: c.accountNo || "",
      IFSC_Code: c.ifscCode || "",
      GST_Number: c.gstNumber || "",
    }));

    const sheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Clients");
    XLSX.writeFile(workbook, "Clients_List.xlsx");
  };

  return (
    <div className="clients-page">
      <div className="clients-shell">
        <div className="clients-hero">
          <div>
            <p className="clients-eyebrow">Relationship Management</p>
            <h3>Client List</h3>
            <span>Manage active clients, services, and engagement records</span>
          </div>
        </div>

        {canDo(user, "clients", "add") && (
          <div className="clients-actions-row">
            <Link to="/admin/addClientLead" className="clients-add-btn">
              Add Client
            </Link>
            <button type="button" className="clients-export-btn" onClick={exportExcel}>
              Export Excel
            </button>
          </div>
        )}

        <div className="clients-search-card">
          <input
            type="text"
            className="clients-search-input"
            placeholder="Search by client id, name, email, phone, service..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="clients-table-card">
          <div className="table-responsive">
            <table className="table text-center align-middle mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Department</th>
                  <th>Service</th>
                  <th>Project Price</th>
                  <th>Enroll Date</th>
                  <th>Account Number</th>
                  <th>IFSC Code</th>
                  <th>GST Number</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {searchedClients.length > 0 ? (
                  searchedClients.map((clients, index) => (
                    <tr key={index}>
                      <td>
                        {canDo(user, "clients", "view") ? (
                          <button
                            type="button"
                            onClick={() => navigate(`/admin/viewclientpage/${clients._id}`)}
                            style={{
                              border: "none",
                              background: "transparent",
                              padding: 0,
                              color: "#0ea5e9",
                              textDecoration: "none",
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                            title={`Open ${clients.leadId}`}
                          >
                            {clients.leadId}
                          </button>
                        ) : (
                          clients.leadId
                        )}
                      </td>
                      <td>{clients.leadName}</td>

                      <td
                        className="text-truncate"
                        style={{ maxWidth: "180px" }}
                        title={clients.emailId}
                      >
                        {clients.emailId}
                      </td>

                      <td>{clients.phoneNo}</td>
                      <td>{clients.department?.deptName}</td>
                      <td>{clients.service?.serviceName}</td>
                      <td>{getProjectPriceForList(clients)}</td>
                      <td>{formatDate(clients.date)}</td>
                      <td>{clients.accountNo || "-"}</td>
                      <td>{clients.ifscCode || "-"}</td>
                      <td>{clients.gstNumber || "-"}</td>

                      <td>
                        <div className="d-flex justify-content-center gap-2 flex-wrap">
                          {canDo(user, "clients", "view") && (
                            <button
                              className="btn btn-sm clients-btn view"
                              onClick={() => navigate(`/admin/viewclientpage/${clients._id}`)}
                            >
                              View
                            </button>
                          )}

                          {canDo(user, "clients", "edit") && (
                            <button
                              className="btn btn-sm clients-btn edit"
                              onClick={() =>
                                navigate(`/admin/updateLeadClient/${clients.leadId}`, {
                                  state: { returnTo: "/admin/client" },
                                })
                              }
                            >
                              Update
                            </button>
                          )}

                          {canDo(user, "clients", "delete") && (
                            <button
                              className="btn btn-sm clients-btn delete"
                              onClick={() => handleDelete(clients.leadId)}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="12" className="text-center text-muted py-3">
                      No clients found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Clients;
